'use client'

import TTSDrawer from '@components/layout/TabBar/drawer/TTSDrawer'
import { UISlideItem } from '@components/ui/UITab'
import ArticleAside from '@page/article/_components/ArticleAside'
import ArticleBody from '@page/article/_components/ArticleBody'
import ArticleHeader from '@page/article/_components/ArticleHeader'
import { ArticleRefProvider } from '@page/article/_components/common/ArticleRefContext'
import IssueHeader from '@page/article/_components/common/IssueHeader'
import SplittedWrapper from '@page/article/_components/common/SplittedWrapper'
import TopicHeader from '@page/article/_components/common/TopicHeader'
import {
	ArticleAreaSequenceResponseVo,
	ArticleIssueTimeLineResponseVo,
	ArticleListResponseVo,
	ArticleNewResponseVo,
	ArticlePreNextResponseVo,
	ArticleRankResponseVo,
	ArticleResponseVo,
	JournalistResponseVo,
	PackageResponseVo,
	PollResponseVo,
} from '@schemas/non-auth'
import { queryArticle } from '@services/api/Article'
import { queryArticleImportant } from '@services/api/Article/Important'
import { queryArticleIssueTimeLine } from '@services/api/Article/IssueTimeLine'
import { queryArticleJournalist } from '@services/api/Article/Journalist'
import { queryArticleNewByArticle } from '@services/api/Article/NewByArticle'
import { queryArticlePoll } from '@services/api/Article/Poll'
import { queryArticlePopularity } from '@services/api/Article/Popularity'
import { ArticlePreNextReqModel, queryArticlePreNext } from '@services/api/Article/PreNext'
import { queryArticleRankView } from '@services/api/Article/RankView'
import { useRecentReadMutation } from '@services/api/Article/RecentRead/mutation'
import { queryArticleRelatedArticle } from '@services/api/Article/RelatedArticle'
import { queryArticleRelatedIssue } from '@services/api/Article/RelatedIssue'
import { ArticleAccessPathCode, PreListPagePathCode } from '@services/api/Article/types'
import { queryArticleAreaSeq } from '@services/api/NewsArticle/AreaSeq'
import { queryStatisticsArticlePV } from '@services/api/Statistics/ArticlePV'
import { AreaDiv, InfluxDiv } from '@services/api/Statistics/types'
import { useAuthControl } from '@store/auth'
import { useGetCookieHeader } from '@store/cookieHeader'
import { useIsAppControl } from '@store/isApp'
import { useTTSControl } from '@store/tts'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { ReactNode, useCallback, useEffect } from 'react'
import ArticleWingAds from './ArticleBody/ArticleWingAds'
import pxToRem from '@utils/pxToRem'
import LoadingContainer from '@components/layout/Loading'
import { scrollToTopAndEnableAutoScrollRestoration } from '@utils/scroll'
import TopBanner from '@components/election/TopBanner'
import { useElectionMeta } from '@services/api/Election'

export interface ArticleContainerProps {
	articleIdx: string
	userToken?: string
	influxDiv?: string
	areaDiv?: string
	areaIdx?: string
	upperCode?: string
	preIdx?: string
	articleAccessPathCode?: ArticleAccessPathCode
	articleAccessIdx?: string
	query?: {
		code?: string
		idx?: string
	}
}

const LIST_PAGE_PATH = ['TOPIC', 'ISSUE', 'SERIES', 'CORNER', 'PROGRAM', 'SECTION']
const ArticleContainerCSR = ({
	articleIdx,
	userToken,
	influxDiv,
	areaDiv,
	areaIdx,
	upperCode,
	preIdx,
	articleAccessPathCode,
	articleAccessIdx,
	query,
}: ArticleContainerProps): ReactNode => {
	const cookieHeader = useGetCookieHeader()
	const pathname = usePathname()
	const { isApp } = useIsAppControl()
	const { isTTSPlaying, setIsTTSPlaying } = useTTSControl()
	const { mutateAsync: mutateRecentRead } = useRecentReadMutation()
	const { auth } = useAuthControl()
	const { data: resArticle } = useQuery({
		...queryArticle({ articleIdx, articleAccessPathCode, articleAccessIdx }, cookieHeader),
		refetchOnMount: 'always',
	})
	const requestQueries = [
		queryArticleAreaSeq(),
		queryArticleJournalist({ articleIdx }, cookieHeader),
		queryArticleRelatedArticle({ articleIdx }),
		queryArticleRelatedIssue({ articleIdx }),
		queryArticlePoll({ articleIdx, nonMemberId: userToken }, cookieHeader),
		queryArticleIssueTimeLine({
			articleIdx,
			articleAccessPathCode,
			articleAccessIdx,
		}),
		queryArticleNewByArticle({
			articleIdx,
			articleAccessPathCode,
			articleAccessIdx,
		}),
		queryArticleImportant({ isArticleArea: true, pageSize: 4 }),
		queryArticleRankView({ pageSize: 5 }),
		queryArticlePopularity({}),
		...(upperCode && LIST_PAGE_PATH.includes(upperCode) && preIdx
			? [
					queryArticlePreNext({
						standardArticleIdx: articleIdx,
						preListPagePathCode: `${upperCode}_LIST_ACCESS` as PreListPagePathCode,
						preListPageAccessIdx: preIdx,
					} as ArticlePreNextReqModel),
			  ]
			: []),
	]
	const responseQueries = useQueries({
		queries: requestQueries,
	})
	const articlePV = useQuery(
		queryStatisticsArticlePV({
			articleIdx,
			influxDiv: influxDiv as InfluxDiv,
			areaDiv: areaDiv as AreaDiv,
			areaIdx,
		}),
	)
	const [
		resAreaSeq,
		resJournalist,
		resRelatedArticle,
		resRelatedIssue,
		resPoll,
		resIssueTimeLine,
		resNewByArticle,
		resImportant,
		resRankView,
		resPopularity,
		resPreNext,
	] = responseQueries?.map((res) => res?.data)
	const response = {
		areaSeq: (resAreaSeq?.data ?? []) as ArticleAreaSequenceResponseVo[],
		article: resArticle?.data as ArticleResponseVo,
		journalist: resJournalist?.data as Array<JournalistResponseVo>,
		relatedArticle: resRelatedArticle?.data as Array<ArticleListResponseVo>,
		relatedIssue: resRelatedIssue?.data as Array<PackageResponseVo>,
		poll: resPoll?.data as PollResponseVo,
		issueTimeLine: resIssueTimeLine?.data as ArticleIssueTimeLineResponseVo,
		newByArticle: resNewByArticle?.data as ArticleNewResponseVo,
		important: resImportant?.data as Array<ArticleListResponseVo>,
		rankView: resRankView?.data as ArticleRankResponseVo,
		popularity: resPopularity?.data as Array<ArticleListResponseVo>,
		preNext: resPreNext?.data as ArticlePreNextResponseVo,
	}
	const setTopicOption = (item: PackageResponseVo) => ({
		label: item.packageName,
		value: item.packageIdx,
		href: `/topic/${response?.article?.topic?.packageIdx}/${item.packageIdx}`,
	})
	const options: UISlideItem[] = response?.article?.topic && [
		{
			label: response?.article?.topic?.packageName,
			value: response?.article?.topic?.packageIdx,
		},
		...response?.article?.topic?.topicLowerIssueList.map(setTopicOption),
	]

	const bodyAreaList = response?.areaSeq
		.filter((item) => item.pcLayoutPosition?.code === 'LEFT')
		?.sort((a, b) => a.areaDispSeq - b.areaDispSeq)
	const asideAreaList = response?.areaSeq
		.filter((item) => item.pcLayoutPosition?.code === 'RIGHT')
		?.sort((a, b) => a.areaDispSeq - b.areaDispSeq)
	const isVisibleComment = response?.article?.isComment

	const updateRecentRead = useCallback(async () => {
		if (auth && articleIdx) {
			await mutateRecentRead({ articleIdx })
		}
	}, [articleIdx, auth])
	useEffect(() => {
		if (response?.article?.articleIdx.startsWith('NB')) {
			setTimeout(() => {
				const { newsAnalysis } = window
				if (newsAnalysis) {
					newsAnalysis?.init('viewarticle', response?.article?.articleIdx, '')
				}
			}, 200)
		}
		return () => {
			updateRecentRead()
			setIsTTSPlaying(false)
		}
	}, [response?.article?.articleIdx])
	const queryClient = useQueryClient()
	const queryKeys = requestQueries.map((q) => q.queryKey) // Changed 'query' to 'q'

	useEffect(() => {
		queryKeys.forEach((key) => {
			queryClient.removeQueries({ queryKey: key })
		})
	}, [pathname])

	useEffect(() => {
		return () => {
			if (isTTSPlaying) {
				setIsTTSPlaying(false)
			}
			setIsTTSPlaying(false)
		}
	}, [])

	useEffect(() => {
		scrollToTopAndEnableAutoScrollRestoration()
	}, [])

	const onPollSubmit = async () => {
		await responseQueries[4].refetch()
	}
	
	const { data: electionData } = useElectionMeta({
		articleIdx,
		articleAccessPathCode,
		articleAccessIdx,
	})

	return (
		<LoadingContainer load={!!response?.article}>
			<>
				{electionData?.data && (
					<TopBanner
						data={electionData.data}
						viewPoint={
							electionData?.data?.topArticleBannerType === 'newsHomeBanner'
								? 'home'
								: 'article'
						}
					/>
				)}
				{response?.article?.issue && (
					<IssueHeader
						title={response?.article?.issue?.packageName}
						href={`/issues/${response?.article?.issue?.packageIdx}`}
						bgImg={
							response?.article?.issue
								? {
										bgMoImgUrl: response?.article?.issue.bgMoImgUrl ?? '',
										bgTbImgUrl: response?.article?.issue.bgTbImgUrl ?? '',
										bgPcImgUrl: response?.article?.issue.bgPcImgUrl ?? '',
								  }
								: undefined
						}
						bgcolor={response?.article?.issue?.bgColor}
					/>
				)}
				{response?.article?.topic && (
					<TopicHeader
						title={response?.article?.topic?.packageName}
						href={`/topic/${response?.article?.topic?.packageIdx}`}
						bgImg={
							response?.article?.topic
								? {
										bgMoImgUrl: response?.article?.topic.bgMoImgUrl ?? '',
										bgTbImgUrl: response?.article?.topic.bgTbImgUrl ?? '',
										bgPcImgUrl: response?.article?.topic.bgPcImgUrl ?? '',
								  }
								: undefined
						}
						bgcolor={response?.article?.topic?.bgColor}
						packageIdx={response?.article?.articleIncludeIssue ?? undefined}
						options={options}
						viewHeader={electionData?.data?.topArticleIssueTopicBannerYn === 'Y'}
					/>
				)}
				<ArticleRefProvider>
					<ArticleHeader
						article={response?.article}
						journalist={response?.journalist}
						commentVisible={isVisibleComment}
						bgImg={
							response?.article?.special
								? {
										bgMoImgUrl: response?.article?.special.bgMoImgUrl ?? '',
										bgTbImgUrl: response?.article?.special.bgTbImgUrl ?? '',
										bgPcImgUrl: response?.article?.special.bgPcImgUrl ?? '',
								  }
								: undefined
						}
						isDark={!!response?.article?.special}
					/>
					<ArticleWingAds />
					<SplittedWrapper
						aside={
							<ArticleAside
								areaSeqList={asideAreaList}
								poll={response?.poll}
								relatedIssue={response?.relatedIssue}
								issueTimeLine={response?.issueTimeLine}
								newByArticle={response?.newByArticle}
								important={response?.important}
								rankView={response?.rankView}
								popularity={response?.popularity}
								onPollSubmit={onPollSubmit}
							/>
						}
						sx={
							response?.article?.special
								? {
										'& .splitted-wrapper': {
											paddingTop: { lg: pxToRem(28), xl: pxToRem(32) },
										},
								  }
								: undefined
						}
					>
						<ArticleBody
							areaSeqList={bodyAreaList}
							article={response?.article}
							journalist={response?.journalist}
							preNext={response?.preNext}
							relatedArticle={response?.relatedArticle}
							query={query}
							commentVisible={isVisibleComment}
						/>
						{isApp && <TTSDrawer text={response?.article?.articleContent ?? ''} />}
					</SplittedWrapper>
				</ArticleRefProvider>
			</>
		</LoadingContainer>
	)
}
export default ArticleContainerCSR
